import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { CreditUsageDashboard } from 'shared/components/Credits';
import { useCredits } from 'hooks/useCredits';
import SkeletonLoader from 'shared/components/ui/SkeletonLoader';
import Button from 'shared/components/Button';
import './Credits.scss';

const Credits = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { balance, subscriptionTier, loading } = useCredits();
  const [pageLoaded, setPageLoaded] = useState(false);

  useEffect(() => {
    // Add a small delay to show the fade-in animation
    const timer = setTimeout(() => setPageLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const formatBalance = balance => {
    if (balance >= 1000) {
      return `${(balance / 1000).toFixed(1)}k`;
    }
    return balance?.toString() || '0';
  };

  const getPlanDescription = tier => {
    switch (tier) {
      case 'FREE':
        return 'Get started with our free plan. Upgrade to unlock more credits and premium features.';
      case 'STARTER':
        return 'Perfect for small teams and growing businesses. Track your usage below.';
      case 'PRO':
        return 'Advanced features for power users. Monitor your usage and optimize performance.';
      case 'PREMIUM':
        return 'Everything you need for large-scale operations. Complete analytics and insights.';
      default:
        return 'Manage your subscription and track your credit usage below.';
    }
  };

  const getActionButtonText = tier => {
    switch (tier) {
      case 'FREE':
        return 'Upgrade Plan';
      default:
        return 'Manage Subscription';
    }
  };

  const handleSubscriptionAction = () => {
    history.push('/profile/subscription');
  };

  if (loading || !pageLoaded) {
    return (
      <div className='credits-page loading'>
        <div className='credits-header'>
          <SkeletonLoader type='basic' height='32px' width='200px' style={{ marginBottom: '8px' }} />
          <SkeletonLoader type='basic' height='16px' width='300px' />
        </div>
        <div className='credits-content'>
          <div className='current-plan-card'>
            <div className='plan-header'>
              <SkeletonLoader type='basic' height='24px' width='120px' />
              <SkeletonLoader type='basic' height='20px' width='80px' borderRadius='12px' />
            </div>
            <div style={{ marginTop: '20px' }}>
              <SkeletonLoader type='basic' height='48px' width='100px' style={{ marginBottom: '8px' }} />
              <SkeletonLoader type='basic' height='14px' width='140px' style={{ marginBottom: '16px' }} />
              <SkeletonLoader type='basic' height='16px' width='100%' />
            </div>
          </div>
          <SkeletonLoader type='basic' height='200px' width='100%' style={{ marginTop: '24px' }} />
        </div>
      </div>
    );
  }

  return (
    <div className='credits-page'>
      <div className='credits-header'>
        <h1 className='credits-title'>{t('Credits & Usage')}</h1>
        <p className='credits-subtitle'>{t('Monitor your credit balance and track usage')}</p>
      </div>

      <div className='credits-content'>
        <div className='current-plan-card'>
          <div className='plan-header'>
            <h2 className='plan-title'>Current Plan</h2>
            <span className={`plan-badge ${subscriptionTier.toLowerCase()}`}>{subscriptionTier}</span>
          </div>

          <div className='credits-info'>
            <div className='credit-amount'>
              <span className='credit-number'>
                <span>{formatBalance(balance)}</span>
              </span>
              <span className='credit-label'>Available Credits</span>
            </div>

            <div className='plan-description'>{getPlanDescription(subscriptionTier)}</div>

            <div className='plan-actions'>
              <Button
                className={`subscription-button ${subscriptionTier === 'FREE' ? 'upgrade-button' : 'manage-button'}`}
                onClick={handleSubscriptionAction}
              >
                {getActionButtonText(subscriptionTier)}
              </Button>
            </div>
          </div>
        </div>

        <CreditUsageDashboard />
      </div>
    </div>
  );
};

export default Credits;
